// src/components/visual/TorchCursor.tsx
import React, { useEffect, useRef, useState } from 'react';

export const TorchCursor: React.FC = () => {
  const [active, setActive] = useState(false);
  const torchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Check for fine pointer (mouse) and absence of reduced motion
    if (typeof window === 'undefined') return;

    const hasFinePointer = window.matchMedia('(pointer: fine) and (hover: hover)').matches;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!hasFinePointer || prefersReducedMotion) {
      setActive(false);
      return;
    }

    setActive(true);

    let rafId: number | null = null;
    let targetX = -1000;
    let targetY = -1000;
    let currentX = -1000;
    let currentY = -1000;

    const onMouseMove = (e: MouseEvent) => {
      targetX = e.clientX;
      targetY = e.clientY;

      if (!rafId) {
        rafId = requestAnimationFrame(animate);
      }
    };

    const animate = () => {
      // Smooth lerp (0.15) for natural fluid lantern tracking
      currentX += (targetX - currentX) * 0.15;
      currentY += (targetY - currentY) * 0.15;

      if (torchRef.current) {
        torchRef.current.style.transform = `translate3d(${currentX}px, ${currentY}px, 0)`;
      }

      // Continue animating until settled
      if (Math.abs(targetX - currentX) > 0.5 || Math.abs(targetY - currentY) > 0.5) {
        rafId = requestAnimationFrame(animate);
      } else {
        rafId = null;
      }
    };

    window.addEventListener('mousemove', onMouseMove, { passive: true });

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  if (!active) return null;

  return (
    <div
      ref={torchRef}
      className="pointer-events-none fixed top-0 left-0 -translate-x-1/2 -translate-y-1/2 z-20 w-[420px] h-[420px] rounded-full will-change-transform"
      style={{
        background: 'radial-gradient(circle, rgba(230, 197, 110, 0.04) 0%, rgba(255, 72, 20, 0.02) 40%, rgba(0, 0, 0, 0) 70%)',
        mixBlendMode: 'screen',
      }}
      aria-hidden="true"
    />
  );
};
